import { removeClientSetsFromDocument } from "@apollo/client/utilities";
import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import { store } from "../utils/StoreContext";
import BundleCustomizer from "../components/BundleCustomizer";
import BundleImages from "../components/BundleImages";
import { PrimaryButton } from "../components/Button";
import DomeHeader from "../components/DomeHeader";
import RatingStars from "../components/RatingStars";
import Breakpoints from "../components/styles/Breakpoints";
import { Colors } from "../components/styles/Colors";
import { Body, Header2 } from "../components/typography";
import richText from "../utils/richText";
import produce from "immer";
import { addOneTimeItem, addSubscriptionItem } from "../utils/StoreActions";
import { trackAddToCart } from "../utils/analytics";
import { getSetSubscriptionData, getPrice } from "../utils/Prive";

import { gsap, ScrollToPlugin } from "gsap/all";
import { SUBS_ENABLED } from "../utils/settings";

gsap.registerPlugin(ScrollToPlugin);

const Wrapper = styled.div`
  background: ${Colors.cream2};
  padding: 40px 32px;

  ${Header2} {
    color: ${Colors.grey};
  }

  ${Body} {
    margin: 16px 0;
  }

  ${PrimaryButton} {
    margin: 20px 0;
    margin-top: 30px;
  }

  @media ${Breakpoints.desktop} {
    padding: 32px 64px 64px;

    display: flex;
    flex-direction: row-reverse;

    align-items: flex-start;

    > div:first-child {
      padding: 0 64px 200px;
    }

    > div:last-child {
      max-width: 550px;
      padding-left: 25px;
      margin-right: 30px;

      ${Header2} {
        margin: 16px 0 40px;
      }

      ${PrimaryButton} {
        width: 200px;
      }
    }
  }
`;

const DetailsBox = styled.div`
  padding: 40px 0;

  > ${Header2}:first-of-type {
    margin: 24px 0 40px;
  }
`;

function calculateTotals(variants, selections) {
  const selected = Object.keys(selections).filter((v) => selections[v]);
  const filtered = variants.filter((v) => selected.includes(v.name));

  const individuals = filtered.reduce(
    (a, v) => a + parseFloat(v.individual.price),
    0
  );
  const subscriptions = filtered.reduce(
    (a, v) => a + parseFloat(v.subscription?.price),
    0
  );

  return [individuals, subscriptions];
}

export default function Bundle({ set }) {
  //create a state so we don't override props objects
  const [productSet, setProductSet] = useState(set);

  const { dispatch } = useContext(store);
  const [cadence, setCadence] = useState(); // months
  const [selectedType, setSelectedType] = useState(
    SUBS_ENABLED ? "SUBSCRIPTION" : "INDIVIDUAL"
  );
  const [selections, setSelections] = useState(
    produce(productSet.variants, (draftVariants) => {
      return draftVariants.reduce((acc, v) => {
        // if (v.name !== "Invisible Daily SPF 30") {
        acc[v.name] = true;
        // }

        return acc;
      }, {});
    })
  );
  //PRIVE
  const [options, setOptions] = useState([]);

  /* START PRIVE CODE */
  useEffect(() => {
    const option = options.find((o) => o.sellingPlanId === cadence);
    const newVariants = productSet.variants.map((v) => ({
      ...v,
      subscription: {
        ...v.subscription,
        price: getPrice(option, v.individual?.priceNumber),
      },
    }));
    setProductSet((old) => ({ ...old, variants: newVariants }));
  }, [options, cadence]);

  function checkVariantDataEquals(data) {
    //get the first selling_plan_group (it should be the only one) and check that every variant
    // has the same
    const offerId = data[0].selling_plan_groups[0].id;
    return data.every(
      (variant) => variant.selling_plan_groups[0].id === offerId
    );
  }
  useEffect(() => {
    getSetSubscriptionData(productSet.variants)
      .then((data) => {
        if (!data) return;
        if (!checkVariantDataEquals(data)) {
          productSet.variants.forEach((variant) => {
            variant.subscription = null;
          });
          //we won't be showing the subscription option if the variants don't have the same offer
          return;
        }
        const offerOptions = data[0]?.selling_plan_groups[0]?.selling_plans;
        setOptions(offerOptions);
        setCadence(
          offerOptions?.find((opt) => opt.deliveryCadenceCount === 3)
            ?.sellingPlanId ||
            offerOptions[0]?.sellingPlanId ||
            null
        );
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  /* END PRIVE CODE */

  function addToCart() {
    Object.entries(selections).forEach(([name, selected]) => {
      if (selected) {
        const variant = productSet.variants.find(
          (variant) => variant.name === name
        );

        const item = {
          name,
          selectedVariant: variant,
        };

        if (selectedType === "SUBSCRIPTION") {
          const option = options?.find((o) => o.sellingPlanId === cadence);
          dispatch(
            addSubscriptionItem(
              item,
              1,
              option?.deliveryCadenceCount,
              true,
              null,
              cadence
            )
          );
        } else {
          dispatch(addOneTimeItem(item, 1, true));
        }
      }
    });
  }

  let reviews = null;
  let rating = null;

  if (productSet.__typename === "ContentfulProductSet") {
    // const reviewItems = set.variants.reduce((accumulator, obj) =[]> [...accumulator, ...obj.product?.reviews], [])
    // reviews = reviewItems
    // const ratingScores = set.variants.map(prod => prod.product?.rating.score)
    // rating = ratingScores.reduce((a, b) => a + b) / ratingScores.length
  } else {
    reviews = productSet?.reviews;
    rating = productSet?.rating.score;
  }

  function scrollTo() {
    gsap.to(window, {
      duration: 1,
      scrollTo: { y: "#product-reviews", offsetY: 100 },
    });
  }

  const individualPrice = new Intl.NumberFormat(process.env.GATSBY_LOCALE, {
    style: "currency",
    currency: process.env.GATSBY_LOCALE === "en-US" ? "USD" : "GBP",
  }).format(calculateTotals(productSet.variants, selections)[0]);

  return (
    <Wrapper>
      <BundleImages images={productSet.customizerImages} />
      <DetailsBox>
        <DomeHeader text="Face + Scalp" />
        <Header2>{productSet.name}</Header2>
        <div>{richText(productSet.description.raw)}</div>
        {/* <RatingStars score={rating} count={reviews.length} scrollTo={scrollTo} /> */}
        {productSet.customizable && (
          <BundleCustomizer
            onSetCadence={setCadence}
            onSetSelected={setSelectedType}
            onSetSelections={setSelections}
            variants={productSet.variants}
            options={options}
          />
        )}
        <PrimaryButton onClick={addToCart}>
          Add to Cart{!SUBS_ENABLED && ` - ${individualPrice}`}
        </PrimaryButton>
      </DetailsBox>
    </Wrapper>
  );
}
